import React, { useContext } from 'react'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Popover from 'react-bootstrap/Popover'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import UserContext from 'context/user-context'
import LockedBadge from 'shared/badges/locked-badge'
import { useSetReportLock } from 'api/hooks/use-report-builder'

export const isReportOwner = (report, user) => (report?.owner?.id ?? report?.user_id) === user?.id

// Locks or unlocks a report and refreshes every cached copy of it (the builder and the index).
export const useToggleReportLock = (report) => {
  const queryClient = useQueryClient()
  const { mutate, isLoading } = useSetReportLock({ options: {} })

  const toggle = (locked) => {
    mutate({ reportId: report.id, locked }, {
      onSuccess: () => {
        queryClient.invalidateQueries(['reports-builder'])
        queryClient.invalidateQueries([`reports/${report.id}`])
        toast.success(locked ? 'Report locked' : 'Report unlocked')
      },
      onError: () => toast.error(locked ? 'Could not lock the report' : 'Could not unlock the report')
    })
  }

  return { toggle, isLoading }
}

// Clicking the lock badge explains the lock; the owner unlocks from ReportActionsMenu. iconOnly drops
// the "Locked" label for dense listings.
const ReportLockBadge = ({ report, className, iconOnly = false }) => {
  const user = useContext(UserContext)
  if (!report?.locked) return null

  const isOwner = isReportOwner(report, user)
  const ownerName = report.user_full_name || 'the owner'
  // Clicks inside the badge must not reach a report card's open-on-click or dnd-kit's drag handle.
  const stop = (e) => e.stopPropagation()

  const popover = (
    <Popover id={`report-lock-${report.id}`} onClick={stop} onPointerDown={stop}>
      <Popover.Header as="h3">Report locked</Popover.Header>
      <Popover.Body>
        {isOwner ? (
          <p className="mb-0">No one can edit this report, including you, until it is unlocked.</p>
        ) : (
          <p className="mb-0">Locked by {ownerName}. Only they can unlock it.</p>
        )}
      </Popover.Body>
    </Popover>
  )

  return (
    <OverlayTrigger trigger="click" placement="bottom" rootClose overlay={popover}>
      <LockedBadge
        as="button"
        type="button"
        className={`border-0 ${className ?? ''}`}
        onClick={stop}
        onPointerDown={stop}
        aria-label="Report locked"
        data-testid="report-lock-badge"
        children={iconOnly ? null : undefined}
      />
    </OverlayTrigger>
  )
}

export default ReportLockBadge
